/** Supported UI locales */
export type Locale = 'ru' | 'en';

/** Default locale — Russian */
export const DEFAULT_LOCALE: Locale = 'ru';

/** All supported locales */
export const SUPPORTED_LOCALES: readonly Locale[] = ['ru', 'en'] as const;

/** localStorage key for persisted locale */
export const LOCALE_STORAGE_KEY = 'roll1-locale';

/**
 * Flat translation dictionary. Keys use dot-notation grouping.
 * All values are strings.
 */
export interface TranslationKeys {
  // === Toolbar ===
  'toolbar.select': string;
  'toolbar.draw': string;
  'toolbar.erase': string;
  'toolbar.shape': string;
  'toolbar.measure': string;
  'toolbar.fog': string;
  'toolbar.note': string;
  'toolbar.portal': string;
  'toolbar.selectDrawing': string;
  'toolbar.text': string;
  'toolbar.aoe': string;
  'toolbar.mapObject': string;
  'toolbar.wall': string;
  'toolbar.token': string;
  'toolbar.token.helper': string;
  'toolbar.undo': string;
  'toolbar.redo': string;

  // === Toolbar Groups ===
  'toolbar.group.select': string;
  'toolbar.group.drawing': string;
  'toolbar.group.fog': string;
  'toolbar.group.measure': string;
  'toolbar.group.pins': string;
  'toolbar.group.text': string;
  'toolbar.group.token': string;
  'toolbar.group.mapObjects': string;
  'toolbar.group.note': string;
  'toolbar.group.objects': string;
  'toolbar.group.aiEdit': string;
  'toolbar.inpaintRect': string;
  'toolbar.inpaintLasso': string;
  'toolbar.detailRect': string;
  'toolbar.initiative': string;
  'toolbar.held': string;
  'toolbar.snapActive': string;
  'toolbar.overflow': string;

  // === Canvas Context Menu ===
  'contextMenu.addToken': string;
  'contextMenu.addPin': string;
  'contextMenu.actAs': string;
  'contextMenu.addText': string;
  'contextMenu.editToken': string;
  'contextMenu.deleteToken': string;
  'contextMenu.copyToken': string;
  'contextMenu.moveToFront': string;
  'contextMenu.moveToBack': string;
  'contextMenu.openEntry': string;
  'contextMenu.movePin': string;
  'contextMenu.deletePin': string;
  'contextMenu.deleteDrawing': string;
  'contextMenu.changeColor': string;
  'contextMenu.viewToken': string;

  // === Drawing Selection ===
  'drawingSelection.stroke': string;
  'drawingSelection.strokes': string;
  'drawingSelection.toolbar': string;
  'drawingSelection.delete': string;

  // === Sidebar Tabs ===
  'sidebar.map': string;
  'sidebar.journal': string;
  'sidebar.notes': string;
  'sidebar.chat': string;
  'sidebar.srd': string;
  'sidebar.initiative': string;
  'sidebar.participants': string;
  'sidebar.ai': string;
  'sidebar.characters': string;

  // === Sidebar Sections (Map tab) ===
  'sidebar.section.pages': string;
  'sidebar.section.tokens': string;
  'sidebar.section.pins': string;
  'sidebar.section.mapObjects': string;

  // === Inspector ===
  'inspector.title': string;
  'inspector.empty': string;
  'inspector.tokenName': string;
  'inspector.tokenHp': string;
  'inspector.tokenAc': string;
  'inspector.tokenAuras': string;
  'inspector.tokenDescription': string;
  'inspector.tokenVision': string;
  'inspector.visionRange': string;
  'inspector.darkvision': string;
  'inspector.drawingColor': string;
  'inspector.drawingWidth': string;
  'inspector.deleteDrawing': string;
  'inspector.textContent': string;
  'inspector.textFontSize': string;
  'inspector.editText': string;
  'inspector.pinTitle': string;
  'inspector.pinVisibility': string;
  'inspector.pinOpenEntry': string;
  'inspector.mapObjectProps': string;
  'inspector.locationTarget': string;
  'inspector.locationNavigate': string;
  'inspector.multiCount': string;
  'inspector.multiDelete': string;
  'inspector.notFound': string;
  'inspector.readOnly': string;

  // === Common Actions ===
  'common.save': string;
  'common.cancel': string;
  'common.delete': string;
  'common.create': string;
  'common.edit': string;
  'common.back': string;
  'common.close': string;
  'common.loading': string;
  'common.search': string;
  'common.reset': string;
  'common.next': string;
  'common.confirm': string;
  'common.name': string;
  'common.none': string;

  // === Auth ===
  'auth.login': string;
  'auth.loginTitle': string;
  'auth.loginBusy': string;
  'auth.register': string;
  'auth.registerTitle': string;
  'auth.registerBusy': string;
  'auth.registerSubmit': string;
  'auth.inviteCode': string;
  'auth.inviteCodeHint': string;
  'auth.inviteCodePlaceholder': string;
  'auth.email': string;
  'auth.password': string;
  'auth.passwordHint': string;
  'auth.displayName': string;
  'auth.hasAccount': string;
  'auth.noAccount': string;
  'auth.showPassword': string;
  'auth.hidePassword': string;
  'auth.profileTitle': string;
  'auth.displayNameLabel': string;
  'auth.saveBusy': string;

  // === Lobby ===
  'lobby.createSession': string;
  'lobby.joinSession': string;
  'lobby.yourName': string;
  'lobby.sessionCode': string;
  'lobby.connecting': string;
  'lobby.enter': string;

  // === Session List ===
  'sessionList.title': string;
  'sessionList.session': string;
  'sessionList.player': string;
  'sessionList.loading': string;
  'sessionList.copied': string;
  'sessionList.copyCode': string;
  'sessionList.today': string;
  'sessionList.yesterday': string;
  'sessionList.daysAgo': string;
  'sessionList.rename': string;
  'sessionList.delete': string;
  'sessionList.copyInvite': string;
  'sessionList.deleteConfirm': string;
  'sessionList.deleteConfirmTitle': string;
  'sessionList.previewComingSoon': string;
  'sessionList.inviteCopied': string;

  // === Chat ===
  'chat.title': string;
  'chat.placeholder': string;
  'chat.rolls': string;
  'chat.helpUsage': string;
  'chat.nameChanged': string;
  'chat.helpRoll': string;
  'chat.helpName': string;
  'chat.helpClear': string;
  'chat.helpGm': string;
  'chat.unknownCommand': string;
  'chat.welcome': string;
  'chat.welcomeShortcuts': string;

  // === Initiative ===
  'initiative.title': string;
  'initiative.rollAll': string;
  'initiative.next': string;
  'initiative.reset': string;
  'initiative.roundStart': string;
  'initiative.round': string;
  'initiative.turn': string;
  'initiative.otherPage': string;

  // === Tokens ===
  'tokens.title': string;
  'tokens.add': string;
  'tokens.boundToSheet': string;
  'tokens.sheetLabel': string;
  'tokens.tokenName': string;
  'tokens.defaultName': string;
  'tokens.added': string;
  'tokens.created': string;

  // === Dice ===
  'dice.title': string;
  'dice.roll': string;

  // === Pages ===
  'pages.title': string;
  'pages.uploadMap': string;
  'pages.clearDrawings': string;
  'pages.aiMap': string;
  'pages.newPage': string;
  'pages.pageNamePrompt': string;
  'pages.showToPlayers': string;
  'pages.preview': string;
  'pages.moveUp': string;
  'pages.moveDown': string;
  'pages.doubleClickRename': string;
  'pages.drawingsCleared': string;
  'pages.deleteConfirmTitle': string;
  'pages.deleteConfirmDesc': string;
  'pages.deleteConfirmDescWithChildren': string;
  'pages.newChildPage': string;
  'pages.collapseAll': string;
  'pages.expandAll': string;
  'pages.childPageNamePrompt': string;

  // === Characters ===
  'characters.title': string;
  'characters.createWizard': string;
  'characters.empty': string;
  'characters.create': string;
  'characters.newCharacter': string;
  'characters.nameLabel': string;
  'characters.namePlaceholder': string;
  'characters.templateLabel': string;
  'characters.typeLabel': string;
  'characters.typePC': string;
  'characters.typeNPC': string;
  'characters.builtIn': string;
  'characters.foreignSheet': string;
  'characters.performanceWarning': string;
  'characters.assignOwner': string;
  'characters.assignOwnerDescription': string;
  'characters.publicFields': string;
  'characters.sheetVisibleTitle': string;
  'characters.sheetHiddenTitle': string;
  'characters.cardVisibilityTitle': string;
  'characters.publicFieldsDescription': string;
  'characters.noOwner': string;
  'characters.createWithoutOwner': string;
  'characters.createAndAssign': string;
  'characters.ungrouped': string;
  'characters.newGroup': string;
  'characters.groupName': string;
  'characters.deleteGroup': string;
  'characters.renameGroup': string;

  // === Participants ===
  'participants.title': string;

  // === Notes ===
  'notes.title': string;
  'notes.empty': string;
  'notes.untitled': string;
  'notes.gmOnly': string;
  'notes.newNote': string;
  'notes.editNote': string;
  'notes.noteTitle': string;
  'notes.noteContent': string;
  'notes.icon': string;
  'notes.color': string;
  'notes.selectColor': string;

  // === Note icons ===
  'noteIcon.pin': string;
  'noteIcon.scroll': string;
  'noteIcon.skull': string;
  'noteIcon.chest': string;
  'noteIcon.star': string;
  'noteIcon.question': string;
  'noteIcon.exclamation': string;
  'noteIcon.camp': string;
  'noteIcon.sword': string;
  'noteIcon.key': string;

  // === Reconnect ===
  'reconnect.message': string;

  // === Connection Status ===
  'status.online': string;
  'status.reconnecting': string;

  // === Workspace Presets ===
  'preset.explore': string;
  'preset.combat': string;
  'preset.prep': string;
  'preset.fullscreen': string;
  'preset.exit': string;

  // === Shortcuts ===
  'shortcuts.title': string;
  'shortcuts.hint': string;
  'shortcuts.sectionTools': string;
  'shortcuts.sectionCamera': string;
  'shortcuts.sectionTokens': string;
  'shortcuts.sectionChat': string;
  'shortcuts.sectionWorkspace': string;
  'shortcuts.toolSelect': string;
  'shortcuts.toolDraw': string;
  'shortcuts.toolEraser': string;
  'shortcuts.toolRuler': string;
  'shortcuts.toolFog': string;
  'shortcuts.toolNote': string;
  'shortcuts.toolPortal': string;
  'shortcuts.snapToggle': string;
  'shortcuts.zoomIn': string;
  'shortcuts.zoomOut': string;
  'shortcuts.fitToScreen': string;
  'shortcuts.exitFullscreen': string;
  'shortcuts.deleteTokens': string;
  'shortcuts.quickSelectToken': string;
  'shortcuts.nextTurn': string;
  'shortcuts.openMapPanel': string;
  'shortcuts.presetExplore': string;
  'shortcuts.presetCombat': string;
  'shortcuts.presetPrep': string;
  'shortcuts.toggleSidebar': string;
  'shortcuts.showShortcuts': string;
  'shortcuts.saveSession': string;
  'shortcuts.loadSession': string;
  'shortcuts.undo': string;
  'shortcuts.redo': string;
  'shortcuts.toolSelectDrawing': string;
  'shortcuts.toolText': string;
  'shortcuts.toolAoe': string;
  'shortcuts.toolMapObject': string;
  'shortcuts.fogBrushDec': string;
  'shortcuts.fogBrushInc': string;
  'shortcuts.copy': string;
  'shortcuts.paste': string;
  'shortcuts.toggleInspector': string;
  'shortcuts.toggleInitiative': string;

  // === Tool Options ===
  'toolOptions.snapToGrid': string;
  'toolOptions.color': string;
  'toolOptions.width': string;
  'toolOptions.mode': string;
  'toolOptions.modeSimple': string;
  'toolOptions.modeAlternating': string;
  'toolOptions.modeEuclidean': string;
  'toolOptions.modeHex': string;
  'toolOptions.gridSizeFt': string;
  'toolOptions.shape': string;
  'toolOptions.shapeRect': string;
  'toolOptions.shapePolygon': string;
  'toolOptions.shapeCircle': string;
  'toolOptions.navigation': string;
  'toolOptions.navFree': string;
  'toolOptions.navConfirm': string;
  'toolOptions.navGmOnly': string;
  'toolOptions.reFog': string;
  'toolOptions.dynamicFogHint': string;
  'toolOptions.clearAllFog': string;
  'toolOptions.coverAllFog': string;
  'toolOptions.shapeLine': string;
  'toolOptions.fogMode': string;
  'toolOptions.brushSize': string;
  'toolOptions.mapSettings': string;
  'toolOptions.gridSize': string;
  'toolOptions.mapScale': string;
  'toolOptions.bgColor': string;
  'toolOptions.deleteMap': string;
  'toolOptions.confirmDeleteMap': string;
  'toolOptions.fogSystem': string;
  'toolOptions.fogManual': string;
  'toolOptions.fogDynamic': string;
  'toolOptions.wallType': string;
  'toolOptions.wallTypeWall': string;
  'toolOptions.wallTypeDoor': string;
  'toolOptions.wallTypeWindow': string;
  'toolOptions.wallTypeTerrain': string;
  'toolOptions.wallTypeSecret': string;
  'toolOptions.wallTypeEthereal': string;
  'toolOptions.aiEditMode': string;
  'toolOptions.sketchToMap': string;

  // === Token Edit Dialog ===
  'tokenEdit.title': string;
  'tokenEdit.name': string;
  'tokenEdit.size': string;
  'tokenEdit.sizeTiny': string;
  'tokenEdit.sizeMedium': string;
  'tokenEdit.sizeLarge': string;
  'tokenEdit.sizeHuge': string;
  'tokenEdit.sizeGargantuan': string;
  'tokenEdit.color': string;
  'tokenEdit.notes': string;
  'tokenEdit.visible': string;
  'tokenEdit.light': string;
  'tokenEdit.lightEnabled': string;
  'tokenEdit.lightBright': string;
  'tokenEdit.lightDim': string;
  'tokenEdit.lightColor': string;
  'tokenEdit.visionType': string;
  'tokenEdit.visionTypeNormal': string;
  'tokenEdit.visionTypeDarkvision': string;

  // === Global Illumination ===
  'page.globalIllumination': string;
  'page.illuminationBright': string;
  'page.illuminationDim': string;
  'page.illuminationDark': string;
  'page.illuminationColor': string;

  // === Conditions Dialog ===
  'conditions.title': string;

  // === Context Menu ===
  'contextMenu.inspect': string;
  'contextMenu.edit': string;
  'contextMenu.conditions': string;
  'contextMenu.hp': string;
  'contextMenu.resize': string;
  'contextMenu.addAura': string;
  'contextMenu.layerUp': string;
  'contextMenu.layerDown': string;
  'contextMenu.copy': string;
  'contextMenu.delete': string;
  'contextMenu.auras': string;
  'contextMenu.fogReset': string;
  'contextMenu.hideToken': string;
  'contextMenu.showToken': string;
  'contextMenu.enableVision': string;
  'contextMenu.disableVision': string;
  'contextMenu.openSheet': string;

  // === Link Context Menu ===
  'linkMenu.edit': string;
  'linkMenu.navigate': string;
  'linkMenu.toggleVisibility': string;
  'linkMenu.delete': string;
  'linkMenu.zone': string;

  // === Aura Editor ===
  'aura.editTitle': string;
  'aura.addTitle': string;
  'aura.radiusFt': string;
  'aura.color': string;
  'aura.opacity': string;
  'aura.label': string;
  'aura.style': string;
  'aura.styleSolid': string;
  'aura.styleDashed': string;
  'aura.stylePulse': string;
  'aura.gmOnly': string;

  // === Location Link Dialog ===
  'locationLink.editTitle': string;
  'locationLink.createTitle': string;
  'locationLink.targetPage': string;
  'locationLink.selectPage': string;
  'locationLink.label': string;
  'locationLink.labelPlaceholder': string;
  'locationLink.style': string;
  'locationLink.stylePortal': string;
  'locationLink.styleDoor': string;
  'locationLink.styleStairs': string;
  'locationLink.styleArea': string;

  // === Template Editor ===
  'templates.title': string;
  'templates.builtIn': string;
  'templates.createTemplate': string;
  'templates.editTemplate': string;
  'templates.newTemplate': string;
  'templates.nameLabel': string;
  'templates.newSection': string;
  'templates.newField': string;
  'templates.addField': string;
  'templates.addSection': string;
  'templates.deleteSection': string;

  // === AI Chat ===
  'ai.title': string;
  'ai.newChat': string;
  'ai.emptyHint': string;
  'ai.thinking': string;
  'ai.placeholder': string;
  'ai.modeSuggest': string;
  'ai.modeDraft': string;
  'ai.modeAuto': string;
  'ai.undo': string;
  'ai.thinkingLabel': string;
  'ai.stop': string;
  'ai.conversationHistory': string;
  'ai.noConversations': string;
  'ai.loadingHistory': string;
  'ai.toolErrorFriendly': string;
  'ai.stoppedByGM': string;
  'ai.openThinkingModal': string;
  'ai.skillApprove': string;
  'ai.skillReject': string;
  'ai.skillRejectConfirm': string;
  'ai.skillApproved': string;
  'ai.skillRejected': string;
  'ai.skillAutoExecuting': string;
  'ai.skillAutoCompleted': string;
  'ai.skillFeedbackPlaceholder': string;
  'ai.skillAddFeedback': string;
  'ai.emptyTiaGreeting': string;
  'ai.emptyPersonaGreeting': string;
  'ai.personaMemory': string;
  'ai.chronicle': string;
  'ai.generating': string;
  'ai.saveAsJournal': string;
  'ai.noPersonaMemory': string;
  'ai.requestChronicle': string;
  'ai.requestEpilogue': string;

  // === AI Tool Labels ===
  'aiTool.getSessionInfo': string;
  'aiTool.getCurrentPage': string;
  'aiTool.listSheets': string;
  'aiTool.getSheet': string;
  'aiTool.createNote': string;
  'aiTool.searchMonsters': string;
  'aiTool.getMonster': string;
  'aiTool.searchClasses': string;
  'aiTool.searchSpells': string;
  'aiTool.createSheet': string;
  'aiTool.updateSheet': string;
  'aiTool.createToken': string;
  'aiTool.bindSheet': string;
  'aiTool.createNpc': string;
  'aiTool.initiativeUpdate': string;
  'aiTool.initiativeNext': string;
  'aiTool.initiativeReset': string;
  'aiTool.applyDamage': string;
  'aiTool.applyHealing': string;
  'aiTool.spellSlotSpend': string;
  'aiTool.concentrationSet': string;
  'aiTool.effectAdd': string;
  'aiTool.effectUpdate': string;
  'aiTool.effectRemove': string;
  'aiTool.repeatingRowAdd': string;
  'aiTool.repeatingRowUpdate': string;
  'aiTool.repeatingRowRemove': string;
  'aiTool.pageActivate': string;
  'aiTool.pageTransfer': string;
  'aiTool.pageCreate': string;
  'aiTool.pageRename': string;
  'aiTool.locationLinkCreate': string;
  'aiTool.fogReveal': string;
  'aiTool.fogReset': string;
  'aiTool.fogToggle': string;
  'aiTool.sheetRest': string;
  'aiTool.abilityAdd': string;
  'aiTool.abilityRemove': string;
  'aiTool.spellbookAdd': string;
  'aiTool.spellbookRemove': string;
  'aiTool.tokenMove': string;
  'aiTool.tokenUpdate': string;
  'aiTool.tokenDelete': string;
  'aiTool.chatMessage': string;
  'aiTool.chatRoll': string;
  'aiTool.rulerShow': string;
  'aiTool.searchJournal': string;
  'aiTool.getJournalEntry': string;
  'aiTool.getRelatedEntries': string;
  'aiTool.createJournalEntry': string;
  'aiTool.updateJournalEntry': string;
  'aiTool.revealJournalEntry': string;
  'aiTool.journalLink': string;
  'aiTool.getSpell': string;
  'aiTool.searchEquipment': string;
  'aiTool.searchMagicItems': string;
  'aiTool.searchRaces': string;
  'aiTool.searchConditions': string;
  'aiTool.createTimelineEvents': string;
  'aiTool.generateCalendar': string;

  // === Timeline Batch Preview (054) ===
  'timelineBatch.title': string;
  'timelineBatch.selectAll': string;
  'timelineBatch.deselectAll': string;
  'timelineBatch.selected': string;
  'timelineBatch.confirm': string;
  'timelineBatch.cancel': string;
  'timelineBatch.titlePlaceholder': string;
  'timelineBatch.reviewButton': string;

  // === Calendar AI Tab (054) ===
  'calendarAi.unavailable': string;
  'calendarAi.worldDescription': string;
  'calendarAi.worldPlaceholder': string;
  'calendarAi.minChars': string;
  'calendarAi.generating': string;
  'calendarAi.generate': string;
  'calendarAi.preview': string;
  'calendarAi.calendarName': string;
  'calendarAi.yearLabel': string;
  'calendarAi.epoch': string;
  'calendarAi.months': string;
  'calendarAi.accept': string;
  'calendarAi.regenerate': string;
  'calendarAi.replaceTitle': string;
  'calendarAi.replaceWarning': string;
  'calendarAi.replaceConfirm': string;

  // === Timeline Link Picker (054) ===
  'linkPicker.add': string;
  'linkPicker.search': string;
  'linkPicker.remove': string;
  'linkPicker.limitReached': string;
  'linkPicker.journal': string;
  'linkPicker.characters': string;
  'linkPicker.pins': string;
  'linkPicker.noResults': string;

  // === AI Map Dialog ===
  'aiMap.title': string;
  'aiMap.description': string;
  'aiMap.promptLabel': string;
  'aiMap.placeholder': string;
  'aiMap.noStrokesHint': string;
  'aiMap.errorGeneration': string;
  'aiMap.errorNetwork': string;
  'aiMap.generating': string;
  'aiMap.preview': string;
  'aiMap.regenerate': string;
  'aiMap.accept': string;
  'aiMap.generate': string;
  'aiMap.altText': string;

  // === AI Inpainting ===
  'inpaint.promptPlaceholder': string;
  'inpaint.generate': string;
  'inpaint.accept': string;
  'inpaint.regenerate': string;
  'inpaint.cancel': string;
  'inpaint.generating': string;
  'inpaint.tooSmall': string;
  'inpaint.tooLarge': string;
  'inpaint.errorGeneration': string;
  'inpaint.errorNetwork': string;

  // === AI Outpainting ===
  'outpaint.title': string;
  'outpaint.directionTop': string;
  'outpaint.directionRight': string;
  'outpaint.directionBottom': string;
  'outpaint.directionLeft': string;
  'outpaint.expandSize': string;
  'outpaint.overlap': string;
  'outpaint.prompt': string;
  'outpaint.generate': string;
  'outpaint.regenerate': string;
  'outpaint.accept': string;
  'outpaint.cancel': string;
  'outpaint.generating': string;
  'outpaint.sizeWarning': string;
  'outpaint.errorGeneration': string;
  'outpaint.errorNetwork': string;
  'outpaint.noMap': string;
  'outpaint.expandMap': string;

  // === AI Detail Pass ===
  'detail.dialogTitle': string;
  'detail.cropLabel': string;
  'detail.semanticLabel': string;
  'detail.promptPlaceholder': string;
  'detail.generate': string;
  'detail.accept': string;
  'detail.regenerate': string;
  'detail.cancel': string;
  'detail.existingLink': string;
  'detail.createNew': string;
  'detail.regenerateExisting': string;
  'detail.errorSmallRegion': string;
  'detail.errorNoMap': string;
  'detail.generating': string;
  'detail.accepting': string;

  // === SRD Panel ===
  'srd.title': string;
  'srd.monsters': string;
  'srd.spells': string;
  'srd.classes': string;
  'srd.races': string;
  'srd.equipment': string;
  'srd.magicItems': string;
  'srd.conditions': string;
  'srd.searchPlaceholder': string;
  'srd.loading': string;
  'srd.results': string;
  'srd.noResults': string;
  'srd.loadMore': string;
  'srd.back': string;
  'srd.allCR': string;
  'srd.allTypes': string;
  'srd.allLevels': string;
  'srd.cantrip': string;
  'srd.level': string;
  'srd.concentrationAbbr': string;
  'srd.allSchools': string;
  'srd.allClasses': string;
  'srd.allCategories': string;
  'srd.allRarities': string;
  'srd.noFeaturesAtLevel': string;
  'srd.token': string;
  'srd.plusSheet': string;

  // === Journal ===
  'journal.empty': string;
  'journal.pcGroup': string;
  'journal.npcGroup': string;
  // Journal panel
  'journal.panel.title': string;
  'journal.panel.createEntry': string;
  'journal.panel.allTypes': string;
  'journal.panel.searchPlaceholder': string;
  'journal.panel.noEntries': string;
  'journal.panel.noEntriesHint': string;
  'journal.panel.noResults': string;
  // Journal types
  'journal.type.npc': string;
  'journal.type.location': string;
  'journal.type.quest': string;
  'journal.type.faction': string;
  'journal.type.handout': string;
  'journal.type.lore': string;
  // Journal sections (player view)
  'journal.section.quests': string;
  'journal.section.myNotes': string;
  'journal.section.knowledge': string;
  'journal.section.onThisMap': string;
  // Journal groups
  'journal.group.ungrouped': string;
  'journal.group.new': string;
  'journal.group.namePlaceholder': string;
  'journal.group.delete': string;
  'journal.group.rename': string;
  // Journal visibility
  'journal.visibility.gm': string;
  'journal.visibility.personal': string;
  'journal.visibility.all': string;
  // Journal context menu
  'journal.menu.edit': string;
  'journal.menu.reveal': string;
  'journal.menu.pin': string;
  'journal.menu.unpin': string;
  'journal.menu.delete': string;
  // Journal delete dialog
  'journal.delete.title': string;
  'journal.delete.description': string;
  'journal.delete.cancel': string;
  'journal.delete.confirm': string;
  // Journal create dialog
  'journal.create.title': string;
  'journal.create.namePlaceholder': string;
  'journal.create.submit': string;
  // Journal preview fields
  'journal.preview.race': string;
  'journal.preview.occupation': string;
  'journal.preview.disposition': string;
  'journal.preview.description': string;
  'journal.preview.secrets': string;
  'journal.preview.personality': string;
  'journal.preview.questHooks': string;
  'journal.preview.alive': string;
  'journal.preview.dead': string;
  'journal.preview.objectives': string;
  'journal.preview.gmNotes': string;
  'journal.preview.rewards': string;
  'journal.preview.attitude': string;
  'journal.preview.content': string;
  'journal.preview.category': string;
  // Journal disposition
  'journal.disposition.friendly': string;
  'journal.disposition.neutral': string;
  'journal.disposition.hostile': string;
  'journal.disposition.unknown': string;

  // Journal editor
  'journal.editor.namePlaceholder': string;
  'journal.editor.gmOnly': string;
  'journal.editor.reveal': string;
  'journal.editor.revealedFields': string;
  'journal.editor.revealedFieldsHint': string;
  'journal.editor.revealConfirm': string;
  'journal.editor.revealConfirmAction': string;
  'journal.editor.pin': string;
  'journal.editor.unpin': string;
  'journal.editor.openEditor': string;
  'journal.editor.visibilityGm': string;
  'journal.editor.visibilityAll': string;
  'journal.editor.tags': string;
  'journal.editor.tagsPlaceholder': string;
  'journal.editor.addItem': string;
  'journal.editor.removeItem': string;
  'journal.editor.edit': string;
  'journal.editor.done': string;
  'journal.editor.more': string;
  'journal.editor.bindToPin': string;
  'journal.editor.bindToSheet': string;
  'journal.editor.bindToPage': string;
  'journal.editor.configureFields': string;
  'journal.editor.noContent': string;

  // Journal form: NPC
  'journal.form.npc.portrait': string;
  'journal.form.npc.race': string;
  'journal.form.npc.occupation': string;
  'journal.form.npc.disposition': string;
  'journal.form.npc.description': string;
  'journal.form.npc.personality': string;
  'journal.form.npc.secrets': string;
  'journal.form.npc.questHooks': string;
  'journal.form.npc.alive': string;

  // Journal form: Location
  'journal.form.location.image': string;
  'journal.form.location.parent': string;
  'journal.form.location.description': string;
  'journal.form.location.secrets': string;
  'journal.form.location.page': string;

  // Journal form: Quest
  'journal.form.quest.status': string;
  'journal.form.quest.questGiver': string;
  'journal.form.quest.description': string;
  'journal.form.quest.gmNotes': string;
  'journal.form.quest.objectives': string;
  'journal.form.quest.objectiveText': string;
  'journal.form.quest.objectiveCompleted': string;
  'journal.form.quest.objectiveHidden': string;
  'journal.form.quest.rewards': string;

  // Journal form: Faction
  'journal.form.faction.description': string;
  'journal.form.faction.attitude': string;
  'journal.form.faction.secrets': string;

  // Journal form: Handout
  'journal.form.handout.content': string;
  'journal.form.handout.image': string;
  'journal.form.handout.gmNotes': string;

  // Journal form: Lore
  'journal.form.lore.category': string;
  'journal.form.lore.content': string;

  // Journal form: Disposition options
  'journal.form.disposition.friendly': string;
  'journal.form.disposition.neutral': string;
  'journal.form.disposition.hostile': string;
  'journal.form.disposition.unknown': string;

  // Journal form: Quest status options
  'journal.form.questStatus.active': string;
  'journal.form.questStatus.completed': string;
  'journal.form.questStatus.failed': string;
  'journal.form.questStatus.hidden': string;

  // Journal: Links
  'journal.links.title': string;
  'journal.links.add': string;
  'journal.links.empty': string;
  'journal.links.relation': string;
  'journal.links.target': string;
  'journal.links.customRelation': string;
  'journal.links.remove': string;
  'journal.links.incoming': string;

  // Journal field labels (for revealedFields checkboxes)
  'journal.field.description': string;
  'journal.field.race': string;
  'journal.field.occupation': string;
  'journal.field.personality': string;
  'journal.field.secrets': string;
  'journal.field.questHooks': string;
  'journal.field.alive': string;
  'journal.field.attitude': string;
  'journal.field.content': string;
  'journal.field.gmNotes': string;
  'journal.field.objectives': string;
  'journal.field.rewards': string;
  'journal.field.status': string;
  'journal.field.category': string;
  'journal.field.image': string;
  'journal.field.disposition': string;

  // Journal: Map integration
  'journal.map.bindNote': string;
  'journal.map.noteNotFound': string;
  'journal.map.goToMap': string;
  'journal.map.pageNotFound': string;

  // Journal: NPC CharacterSheet
  'journal.npc.bindSheet': string;
  'journal.npc.linkedSheet': string;
  'journal.npc.sheetDeleted': string;
  'journal.npc.openSheet': string;
  'journal.npc.unbindSheet': string;

  // Journal: Relation labels
  'journal.relation.lives_in': string;
  'journal.relation.quest_giver': string;
  'journal.relation.member_of': string;
  'journal.relation.enemy_of': string;

  // === Sheet View ===
  'sheet.speed': string;
  'sheet.initiativeMod': string;
  'sheet.tokenDescription': string;
  'sheet.tokenDescriptionPlaceholder': string;
  'sheet.tokenDescriptionHint': string;
  'sheet.fullForm': string;
  'sheet.statBlock': string;
  'sheet.tokenActions': string;
  'sheet.actionName': string;
  'sheet.actionExpression': string;
  'sheet.addAction': string;
  'sheet.hitDice': string;
  'sheet.spendHitDie': string;
  'sheet.featuresTraits': string;
  'sheet.uses': string;
  'sheet.rest': string;
  'sheet.shortRest': string;
  'sheet.longRest': string;
  'sheet.levelUp': string;
  'sheet.encumbered': string;
  'sheet.rollTooltip': string;
  'sheet.proficiency': string;

  // === Character Wizard ===
  'wizard.createTitle': string;
  'wizard.step': string;
  'wizard.createCharacter': string;
  'wizard.stepRace': string;
  'wizard.stepClass': string;
  'wizard.stepAbilities': string;
  'wizard.stepSkills': string;
  'wizard.stepEquipment': string;
  'wizard.stepSpells': string;
  'wizard.stepReview': string;

  // === Conditions (D&D) ===
  'condition.blinded': string;
  'condition.charmed': string;
  'condition.deafened': string;
  'condition.frightened': string;
  'condition.grappled': string;
  'condition.incapacitated': string;
  'condition.invisible': string;
  'condition.paralyzed': string;
  'condition.petrified': string;
  'condition.poisoned': string;
  'condition.prone': string;
  'condition.restrained': string;
  'condition.stunned': string;
  'condition.unconscious': string;
  'condition.exhaustion': string;
  'condition.concentration': string;

  // === Toast messages ===
  'toast.mapUploaded': string;
  'toast.mapUploadError': string;
  'toast.tokenAdded': string;
  'toast.tokenCreated': string;
  'toast.sessionSaved': string;
  'toast.sessionLoaded': string;

  // === Server Error Codes ===
  'error.INVALID_TOKEN': string;
  'error.USER_NOT_FOUND': string;
  'error.sessionError': string;
  'error.SESSION_NOT_FOUND': string;
  'error.NOT_AUTHORIZED': string;
  'error.UNAUTHORIZED': string;
  'error.NO_SKETCH': string;
  'error.INVALID_PROMPT': string;
  'error.PAGE_NOT_FOUND': string;
  'error.AI_UNAVAILABLE': string;
  'error.TIMEOUT': string;
  'error.AI_ERROR': string;
  'error.AUTH_REQUIRED': string;
  'error.BAD_REQUEST': string;
  'error.NOT_FOUND': string;
  'error.FORBIDDEN': string;

  // === Pluralization ===
  'plural.participant_one': string;
  'plural.participant_few': string;
  'plural.participant_many': string;
  'plural.day_one': string;
  'plural.day_few': string;
  'plural.day_many': string;

  // === AppShell session bar ===
  'session.label': string;
  'session.participantsLabel': string;
  'session.disconnect': string;
  'session.expandSidebar': string;
  'session.collapseSidebar': string;
  'session.detachPanel': string;
  'session.panelDetached': string;
  'session.roleGM': string;
  'session.rolePlayer': string;
  'session.connectionLost': string;
  'session.connectionRestored': string;
  'session.sessionCreated': string;
  'session.sessionJoined': string;
  'session.participantJoined': string;
  'session.participantLeft': string;
  'session.pageCreated': string;
  'session.sheetCreated': string;

  // === Input Dialog ===
  'inputDialog.cancel': string;
  'inputDialog.confirm': string;

  // === Confirm Dialog ===
  'confirmDialog.replaceBinding': string;
  'confirmDialog.replaceBindingDesc': string;
  'confirmDialog.replace': string;

  // === AppDialogs ===
  'appDialogs.loading': string;

  // === File upload ===
  'upload.mapLoaded': string;
  'upload.mapError': string;
  'upload.tokenAdded': string;

  // === Misc ===
  'misc.drawingsCleared': string;
  'misc.sessionSaved': string;
  'misc.sessionLoaded': string;
  'misc.loadError': string;
  'misc.hpDialogTitle': string;
  'misc.resizeDialogTitle': string;
  'misc.newTokenDialogTitle': string;

  // === AI Confirm Card ===
  'aiConfirm.details': string;
  'aiConfirm.cancel': string;
  'aiConfirm.create': string;

  // === Chat Roll ===
  'chatRoll.crit': string;
  'chatRoll.fumble': string;
  'chatRoll.damage': string;

  // === Condition Tooltip ===
  'conditionTooltip.noDescription': string;

  // === Confirm Dialog defaults ===
  'confirmDialog.confirm': string;
  'confirmDialog.cancel': string;

  // === Effects Panel ===
  'effects.title': string;
  'effects.empty': string;
  'effects.enable': string;
  'effects.disable': string;
  'effects.remove': string;
  'effects.addEffect': string;
  'effects.source': string;
  'effects.target': string;
  'effects.mode': string;
  'effects.value': string;
  'effects.add': string;

  // === AI Map generation count ===
  'aiMap.generationCount': string;

  // === Note Editor placeholders ===
  'notes.titlePlaceholder': string;
  'notes.contentPlaceholder': string;
  'notes.noContent': string;
  'notes.quickCreate': string;
  'notes.setParent': string;
  'notes.moveToRoot': string;

  // === Map Pins ===
  'pins.title': string;
  'pins.empty': string;
  'pins.newPin': string;
  'pins.editPin': string;
  'pins.icon': string;
  'pins.color': string;
  'pins.selectColor': string;
  'pins.gmOnly': string;
  'pins.ownerOnly': string;
  'pins.visibility': string;
  'pins.linkedEntry': string;
  'pins.orphan': string;
  'pins.unlinkEntry': string;
  'pins.linkEntry': string;
  'pins.delete': string;
  'pins.deleteConfirm': string;
  'pins.created': string;
  'pins.deleted': string;
  'pins.limitReached': string;
  'pins.quickCreate': string;
  'pins.noEntry': string;
  'pins.journalEntry': string;
  'pins.selectEntry': string;
  'pins.visibilityAll': string;

  // === Fullscreen Mini Panel ===
  'fullscreen.round': string;
  'fullscreen.nextTurn': string;
  'fullscreen.exitFullscreen': string;

  // === Location Breadcrumb ===
  'breadcrumb.back': string;

  // === Navigate Request Toast ===
  'navigate.wantsToGoTo': string;
  'navigate.deny': string;
  'navigate.approve': string;

  // === NPC Stat Block ===
  'npc.conditions': string;
  'npc.attacks': string;
  'npc.rollAbility': string;
  'npc.checkAbility': string;
  'npc.rollAttack': string;
  'npc.abilityStr': string;
  'npc.abilityDex': string;
  'npc.abilityCon': string;
  'npc.abilityInt': string;
  'npc.abilityWis': string;
  'npc.abilityCha': string;

  // === PopoutWindow ===
  'popout.close': string;

  // === Portrait Upload ===
  'portrait.fileTooLarge': string;
  'portrait.networkError': string;
  'portrait.alt': string;

  // === Repeating Section ===
  'repeating.add': string;
  'repeating.empty': string;
  'repeating.remove': string;
  'repeating.showAll': string;
  'repeating.hidden': string;

  // === Selection Bar ===
  'selectionBar.inspect': string;
  'selectionBar.edit': string;
  'selectionBar.conditions': string;
  'selectionBar.delete': string;

  // === Sheet Modal ===
  'sheetModal.popout': string;
  'sheetModal.templateNotFound': string;

  // === Spell Slots Widget ===
  'spells.slotsTitle': string;
  'spells.noSlots': string;
  'spells.level': string;
  'spells.spendSlot': string;
  'spells.spent': string;

  // === Token Inspect Popup ===
  'tokenInspect.gmNotes': string;
  'tokenInspect.openSheet': string;
  'tokenInspect.ac': string;

  // === Sidebar detached tab ===
  'sidebar.detached': string;
  'sidebar.notification': string;

  // === SrdPanel filter placeholders ===
  'srd.filterType': string;
  'srd.filterLevel': string;
  'srd.filterSchool': string;
  'srd.filterClass': string;
  'srd.filterCategory': string;
  'srd.filterRarity': string;

  // === Dialog close ===
  'dialog.close': string;

  // === Token Panel preset ===
  'tokens.presetToken': string;

  // === Session Page Tabs ===
  'sessionPage.sessionsTab': string;
  'sessionPage.profileTab': string;

  // === App (lobby) ===
  'app.profile': string;
  'app.logout': string;
  'app.joinByCode': string;

  // === AI Error Messages ===
  'aiError.serviceUnavailable': string;
  'aiError.rateLimit': string;
  'aiError.networkError': string;
  'aiError.timeout': string;
  'aiError.sendError': string;
  'aiError.gmOnly': string;
  'aiError.undoExpired': string;
  'aiError.voicePermissionDenied': string;
  'aiError.voiceNotAvailable': string;
  'aiError.tokenBudget': string;
  'aiError.playerAiDisabled': string;

  // === Player AI ===
  'playerAi.title': string;
  'playerAi.inputPlaceholder': string;
  'playerAi.send': string;
  'playerAi.newChat': string;

  // === Game System ===
  'gameSystem.label': string;
  'gameSystem.dnd5e': string;
  'gameSystem.pf2e': string;
  'gameSystem.generic': string;
  'srd.unavailable': string;

  // === Auth Errors ===
  'authError.loginError': string;
  'authError.registerError': string;
  'authError.updateError': string;
  'authError.networkError': string;

  // === Chat ===
  'chat.defaultName': string;

  // === Chat Sound Settings ===
  'chat.sound.settings': string;
  'chat.sound.volume': string;
  'chat.sound.mute': string;
  'chat.sound.chatEnabled': string;
  'chat.sound.diceEnabled': string;

  // === Ruler / Measurement ===
  'ruler.ft': string;
  'ruler.total': string;

  // === Token copy ===
  'tokens.copySuffix': string;

  // === Sidebar: Alerts ===
  'sidebar.alerts': string;

  // === Sidebar: Timeline ===
  'sidebar.timeline': string;

  // === Alerts ===
  'alerts.title': string;
  'alerts.create': string;
  'alerts.edit': string;
  'alerts.delete': string;
  'alerts.trigger': string;
  'alerts.emptyTitle': string;
  'alerts.emptyHint': string;
  'alerts.presets': string;
  'alerts.triggerActive': string;
  'alerts.nameLabel': string;
  'alerts.visualLabel': string;
  'alerts.soundLabel': string;
  'alerts.layoutLabel': string;
  'alerts.durationLabel': string;
  'alerts.volume': string;
  'alerts.triggerLabel': string;
  'alerts.customEventPlaceholder': string;
  'alerts.triggerEnabledLabel': string;
  'alerts.deleteConfirmTitle': string;
  'alerts.deleteConfirmDesc': string;

  // Alert presets
  'alerts.preset.critical-hit': string;
  'alerts.preset.character-death': string;
  'alerts.preset.combat-start': string;
  'alerts.preset.new-location': string;

  // Alert layouts
  'alerts.layout.fullscreen': string;
  'alerts.layout.banner-top': string;
  'alerts.layout.banner-bottom': string;

  // Alert duration
  'alerts.duration.auto': string;
  'alerts.duration.manual': string;

  // Alert triggers
  'alerts.trigger.manual': string;
  'alerts.trigger.initiative:start': string;
  'alerts.trigger.initiative:round': string;
  'alerts.trigger.token:death': string;
  'alerts.trigger.roll:critical': string;
  'alerts.trigger.roll:fumble': string;
  'alerts.trigger.page:activate': string;
  'alerts.trigger.custom': string;

  // Alert sections
  'alerts.mediaSection': string;
  'alerts.settingsSection': string;
  'alerts.triggerSection': string;
  'alerts.limitCounter': string;

  // Alert limits
  'alerts.limitWarning': string;
  'alerts.limitReached': string;
  'alerts.triggerLimitReached': string;

  // Alert sound settings
  'alerts.sound.settings': string;
  'alerts.sound.volume': string;
  'alerts.sound.mute': string;

  // Alert errors
  'alerts.error.nameRequired': string;
  'alerts.error.durationRange': string;
  'alerts.error.customEventRequired': string;
  'alerts.error.fileTooLarge': string;
  'alerts.error.uploadFailed': string;

  // === Stickers ===
  'sticker.title': string;
  'sticker.cooldown': string;
  'sticker.cooldownUnit': string;
  'sticker.createPack': string;
  'sticker.connectPack': string;
  'sticker.editPack': string;
  'sticker.deletePack': string;
  'sticker.disconnectPack': string;
  'sticker.emptyTitle': string;
  'sticker.emptyHint': string;
  'sticker.noStickers': string;
  'sticker.packName': string;
  'sticker.packNamePlaceholder': string;
  'sticker.stickerCount': string;
  'sticker.uploadHint': string;
  'sticker.uploadDragHint': string;
  'sticker.uploadBrowse': string;
  'sticker.limitReached': string;
  'sticker.connectTitle': string;
  'sticker.connectButton': string;
  'sticker.allConnected': string;
  'sticker.noPacks': string;
  'sticker.createFirst': string;
  'sticker.deleteConfirmTitle': string;
  'sticker.deleteConfirmMessage': string;
  'sticker.disconnectConfirmTitle': string;
  'sticker.disconnectConfirmMessage': string;
  'sticker.builtIn': string;
  'sticker.custom': string;
  'sticker.done': string;
  'sticker.cancel': string;
  'sticker.close': string;
  'sticker.unavailable': string;
  'sticker.emptyPack': string;
  'sticker.error.formatInvalid': string;
  'sticker.error.fileTooLarge': string;
  'sticker.error.tooManyUploads': string;
  'sticker.error.packNotFound': string;
  'sticker.error.uploadFailed': string;
  'sticker.error.limitExceeded': string;
  'sticker.mediaTab.alerts': string;
  'sticker.mediaTab.stickers': string;

  // === Release Notes ===
  'releaseNotes.badgeNew': string;
  'releaseNotes.title': string;
  'releaseNotes.menuItem': string;
  'releaseNotes.sectionNew': string;
  'releaseNotes.sectionFix': string;
  'releaseNotes.sectionImprovement': string;
  'releaseNotes.currentVersion': string;
  'releaseNotes.learnMore': string;
  'releaseNotes.version': string;
  'releaseNotes.empty': string;

  // === Mobile ===
  'mobile.overflowMenu': string;
  'mobile.aiChat': string;
  'mobile.chat': string;
  'mobile.sheet': string;
  'mobile.initiative': string;
  'mobile.dice': string;
  'mobile.voiceInput': string;
  'mobile.voiceListening': string;
  'mobile.voiceError': string;
  'mobile.voiceNotSupported': string;

  'mobile.tokenPlaced': string;

  // === Mobile Canvas (024-2) ===
  'mobile.tokenPopup.hp': string;
  'mobile.tokenPopup.close': string;
  'mobile.tokenPopup.conditions': string;

  // === Mobile Touch Interactions (024-3) ===
  'mobile.characterSheet.aiHint': string;
  'mobile.characterSheet.aiHintDismiss': string;

  // === PWA (024-4) ===
  'pwa.offlineBanner': string;
  'pwa.installPrompt': string;
  'pwa.installButton': string;
  'pwa.installDismiss': string;

  // === Push Notifications (024-5) ===
  'push.promptTitle': string;
  'push.promptAllow': string;
  'push.promptDismiss': string;

  // === Bug Report ===
  'bugReport.title': string;
  'bugReport.description': string;
  'bugReport.titleLabel': string;
  'bugReport.titlePlaceholder': string;
  'bugReport.descriptionLabel': string;
  'bugReport.descriptionPlaceholder': string;
  'bugReport.cancel': string;
  'bugReport.submit': string;
  'bugReport.submitted': string;
  'bugReport.menuItem': string;
  'bugReport.rateLimited': string;
  'bugReport.retryIn': string;

  // === Undo/Redo (032-A) ===
  'undo.tokenMove': string;

  // === AoE Templates (037) ===
  'aoe.cone': string;
  'aoe.sphere': string;
  'aoe.cube': string;
  'aoe.line': string;
  'aoe.cylinder': string;
  'aoe.size': string;
  'aoe.color': string;
  'aoe.label': string;
  'aoe.clearAll': string;
  'aoe.autoRemove': string;
  'aoe.durationNone': string;
  'aoe.duration1Round': string;
  'aoe.duration1Min': string;
  'aoe.duration10Min': string;

  // === Roll Card (037) ===
  'rollCard.hit': string;
  'rollCard.miss': string;
  'rollCard.damage': string;
  'rollCard.crit': string;
  'rollCard.fumble': string;
  'rollCard.breakdown': string;
  'rollCard.audit': string;

  // === Action HUD (037) ===
  'actionHud.attack': string;
  'actionHud.spell': string;
  'actionHud.ability': string;
  'actionHud.other': string;
  'actionHud.compact': string;
  'actionHud.expanded': string;
  'actionHud.icon': string;
  'actionHud.color': string;

  // === Death Save (037) ===
  'deathSave.dying': string;
  'deathSave.stable': string;
  'deathSave.dead': string;
  'deathSave.alive': string;
  'deathSave.roll': string;
  'deathSave.success': string;
  'deathSave.failure': string;
  'deathSave.nat20': string;
  'deathSave.nat1': string;
  'deathSave.stabilized': string;
  'deathSave.died': string;

  // === Concentration (037) ===
  'concentration.broken': string;
  'concentration.maintained': string;
  'concentration.dc': string;

  // === Condition: Dead (037) ===
  'condition.dead': string;

  // === Mobile Content Access (034-phase-4) ===
  'mobile.switchLanguage': string;
  'mobile.notes': string;
  'mobile.journal': string;
  'mobile.srd': string;
  'sheetModal.clickToEditName': string;

  // === Map Objects ===
  'mapObject.empty': string;
  'mapObject.createObject': string;
  'mapObject.title': string;
  'mapObject.visibleAll': string;
  'mapObject.gmOnly': string;
  'mapObject.noun': string;
  'mapObject.label': string;
  'mapObject.description': string;
  'mapObject.category': string;
  'mapObject.properties': string;
  'mapObject.cover': string;
  'mapObject.coverNone': string;
  'mapObject.coverHalf': string;
  'mapObject.coverThreeQuarter': string;
  'mapObject.coverFull': string;
  'mapObject.state': string;
  'mapObject.stateNone': string;
  'mapObject.stateClosed': string;
  'mapObject.stateOpen': string;
  'mapObject.stateBroken': string;
  'mapObject.stateLit': string;
  'mapObject.stateExtinguished': string;
  'mapObject.traversable': string;
  'mapObject.difficultTerrain': string;
  'mapObject.hp': string;
  'mapObject.maxHp': string;
  'mapObject.locked': string;
  'mapObject.damage': string;
  'mapObject.light': string;
  'mapObject.lightRadius': string;
  'mapObject.lightColor': string;
  'mapObject.display': string;
  'mapObject.displayColor': string;
  'mapObject.icon': string;
  'mapObject.opacity': string;
  'mapObject.visibility': string;
  'mapObject.visibilityGm': string;
  'mapObject.visibilityAll': string;
  'mapObject.interactable': string;
  'mapObject.targetPage': string;
  'mapObject.catFurniture': string;
  'mapObject.catTerrain': string;
  'mapObject.catHazard': string;
  'mapObject.catDoor': string;
  'mapObject.catContainer': string;
  'mapObject.catLight': string;
  'mapObject.catCustom': string;
  'mapObject.coverHalfBadge': string;
  'mapObject.coverThreeQuarterBadge': string;
  'mapObject.coverFullBadge': string;

  // === Fog Confirm Dialogs ===
  'fog.confirmCoverAll.title': string;
  'fog.confirmCoverAll.description': string;
  'fog.confirmClearAll.title': string;
  'fog.confirmClearAll.description': string;
  'fog.confirm.confirm': string;
  'fog.confirm.cancel': string;

  // === Quick Access Panel ===
  'quickAccess.title': string;
  'quickAccess.empty': string;
  'quickAccess.add': string;
  'quickAccess.remove': string;
  'quickAccess.collapse': string;
  'quickAccess.expand': string;
  'quickAccess.close': string;
  'quickAccess.categoryTool': string;
  'quickAccess.categoryRoll': string;
  'quickAccess.categoryMacro': string;
  'quickAccess.rollLabel': string;
  'quickAccess.rollFormula': string;
  'quickAccess.macroLabel': string;
  'quickAccess.macroCommands': string;
  'quickAccess.invalidFormula': string;
  'quickAccess.limitReached': string;
  'quickAccess.duplicateTool': string;
  'quickAccess.macroCommandCount': string;
  'quickAccess.categoryAction': string;
  'quickAccess.categoryTab': string;
  'quickAccess.sectionPinned': string;
  'quickAccess.sectionRecent': string;
  'quickAccess.noSheetsWithActions': string;
  'quickAccess.duplicateAction': string;
  'quickAccess.duplicateTab': string;
  'shortcuts.toggleQuickAccess': string;

  // === Settings Dialog ===
  'settings.title': string;
  'settings.tab.ai': string;
  'settings.tab.campaign': string;
  'settings.tab.display': string;
  'settings.tab.combat': string;
  'settings.tab.permissions': string;
  'settings.tab.resources': string;
  'settings.resources.gmOnly': string;
  'settings.resources.loadError': string;
  'settings.resources.tokens30d': string;
  'settings.resources.players': string;
  'settings.resources.tokensAllTime': string;
  'settings.resources.allTimeHint': string;
  'settings.resources.interactions30d': string;
  'settings.resources.chartTitle': string;
  'settings.resources.tokensUnit': string;
  'settings.resources.interactionsUnit': string;
  'settings.resources.tableToggle': string;
  'settings.resources.colDay': string;
  'settings.resources.colTokens': string;
  'settings.resources.colInteractions': string;
  'settings.ai.worldDescription': string;
  'settings.ai.worldDescriptionPlaceholder': string;
  'settings.ai.houseRules': string;
  'settings.ai.houseRulesPlaceholder': string;
  'settings.ai.tone': string;
  'settings.ai.gmInstructions': string;
  'settings.ai.gmInstructionsPlaceholder': string;
  'settings.campaign.measurementMode': string;
  'settings.campaign.gridSizeFt': string;
  'settings.campaign.navigationMode': string;
  'settings.campaign.navFree': string;
  'settings.campaign.navConfirm': string;
  'settings.campaign.navGmOnly': string;
  'settings.campaign.importCampaign': string;
  'settings.display.gridStyle': string;
  'settings.display.gridLines': string;
  'settings.display.gridDots': string;
  'settings.display.gridNone': string;
  'settings.display.gridOpacity': string;
  'settings.display.sizeTiny': string;
  'settings.display.sizeSmall': string;
  'settings.display.sizeMedium': string;
  'settings.display.sizeLarge': string;
  'settings.display.sizeHuge': string;
  'settings.display.sizeGargantuan': string;
  'settings.display.ambientOverlay': string;
  'settings.display.hpBarVisibility': string;
  'settings.display.hpAlways': string;
  'settings.display.hpHover': string;
  'settings.display.hpGmOnly': string;
  'settings.display.hpNever': string;
  'settings.display.defaultTokenSize': string;
  'settings.combat.autoRollInitiative': string;
  'settings.combat.autoRollInitiativeDesc': string;
  'settings.combat.confirmDamage': string;
  'settings.combat.confirmDamageDesc': string;
  'settings.combat.critRange': string;
  'settings.combat.critRangeDesc': string;
  'settings.combat.deathSavesEnabled': string;
  'settings.combat.deathSavesEnabledDesc': string;
  'settings.permissions.description': string;
  'settings.permissions.moveOtherTokens': string;
  'settings.permissions.moveOtherTokensDesc': string;
  'settings.permissions.drawOnMap': string;
  'settings.permissions.drawOnMapDesc': string;
  'settings.permissions.addTokens': string;
  'settings.permissions.addTokensDesc': string;
  'settings.permissions.createNotes': string;
  'settings.permissions.createNotesDesc': string;
  'settings.permissions.useAi': string;
  'settings.permissions.useAiDesc': string;
  'settings.permissions.playerAiRate': string;
  'settings.permissions.playerAiRateDesc': string;
  'settings.permissions.playerAiBudget': string;
  'settings.permissions.playerAiBudgetDesc': string;
  'settings.permissions.unlimited': string;
  'settings.permissions.allowPlayerStickers': string;
  'settings.permissions.allowPlayerStickersDesc': string;
  'settings.permissions.viewOtherSheets': string;
  'settings.permissions.viewOtherSheetsDesc': string;
  'settings.permissions.viewNone': string;
  'settings.permissions.viewBasic': string;
  'settings.permissions.viewFull': string;

  'settings.ai.excludedEntities': string;
  'settings.ai.searchEntities': string;
  'settings.ai.sectionPriority': string;
  'settings.ai.briefingFocus': string;
  'settings.ai.briefingFocusDesc': string;
  'settings.ai.briefingFocusPlaceholder': string;
  'settings.ai.briefingActiveNpcs': string;
  'settings.ai.briefingActiveNpcsDesc': string;
  'settings.ai.briefingActiveNpcsPlaceholder': string;
  'settings.ai.snapshotsLabel': string;
  'toolOptions.allSettings': string;
  'combat.confirmDamagePrompt': string;
  'permissions.denied.drawOnMap': string;
  'permissions.denied.addTokens': string;
  'permissions.denied.createNotes': string;

  // === Server Error Codes (command:error) ===
  'error.not_found': string;
  'error.unauthorized': string;
  'error.forbidden': string;
  'error.invalid_input': string;
  'error.invalid_name': string;
  'error.invalid_data': string;
  'error.invalid_type': string;
  'error.invalid_tags': string;
  'error.invalid_visibility': string;
  'error.duplicate': string;
  'error.duplicate_link': string;
  'error.self_link': string;
  'error.self_reference': string;
  'error.circular_reference': string;
  'error.LINK_NOT_FOUND': string;
  'error.links_limit': string;
  'error.PAGE_LIMIT': string;
  'error.CANNOT_DELETE_LAST': string;
  'error.INVALID_PAGE_LIST': string;
  'error.SAME_PAGE': string;
  'error.TOKEN_NOT_FOUND': string;
  'error.SHEET_NOT_FOUND': string;
  'error.alert_limit': string;
  'error.trigger_limit': string;
  'error.unknown_preset': string;
  'error.invalid_label': string;
  'error.invalid_color': string;
  'error.invalid_icon': string;
  'error.invalid_content': string;
  'error.invalid_title': string;
  'error.note_limit': string;
  'error.parent_not_found': string;
  'error.invalid_area': string;
  'error.invalid_description': string;
  'error.invalid_terrain': string;
  'error.invalid_cover': string;
  'error.TARGET_PAGE_NOT_FOUND': string;
  'error.NAVIGATE_REQUEST_NOT_FOUND': string;
  'error.GM_ONLY_NAVIGATION': string;
  'error.builtin_template': string;
  'error.in_use': string;
  'error.validation_error': string;
  'error.invalid_state': string;
  'error.death_saves_disabled': string;
  'error.system_not_supported': string;
  'error.exhausted': string;
  'error.no_hit_dice': string;
  'error.no_spell_slots': string;
  'error.no_pact': string;
  'error.not_repeating': string;
  'error.invalid_action': string;
  'error.invalid_expression': string;
  'error.invalid_level': string;
  'error.invalid_mode': string;
  'error.not_draft': string;
  'error.session_limit': string;
  'error.session_full': string;
  'error.active_limit': string;
  'error.timeline_limit': string;
  'error.tags_limit': string;
  'error.PIN_LIMIT': string;
  'error.PIN_NOT_FOUND': string;
  'error.WALL_NOT_FOUND': string;
  'error.WALL_INVALID': string;
  'error.WALL_LIMIT': string;
  'error.WALL_NOT_DOOR': string;
  'error.WALL_CHAIN_MIN_POINTS': string;
  'error.INVALID_ILLUMINATION': string;
  'error.invalid_condition': string;
  'error.invalid_transition': string;
  'error.restore_failed': string;
  'error.max_level': string;
  'error.trigger_disabled': string;
  'error.internal_error': string;

  // === Timeline ===
  'timeline.eventType.sessionStart': string;
  'timeline.eventType.sessionRecap': string;
  'timeline.eventType.encounter': string;
  'timeline.eventType.discovery': string;
  'timeline.eventType.plot': string;
  'timeline.eventType.travel': string;
  'timeline.eventType.rest': string;
  'timeline.eventType.downtime': string;
  'timeline.eventType.character': string;
  'timeline.eventType.custom': string;
  'timeline.eventType.chapter': string;
  'timeline.timeOfDay.dawn': string;
  'timeline.timeOfDay.morning': string;
  'timeline.timeOfDay.midday': string;
  'timeline.timeOfDay.afternoon': string;
  'timeline.timeOfDay.dusk': string;
  'timeline.timeOfDay.evening': string;
  'timeline.timeOfDay.night': string;
  'timeline.timeOfDay.midnight': string;
  'timeline.form.editTitle': string;
  'timeline.form.newTitle': string;
  'timeline.form.titleLabel': string;
  'timeline.form.titlePlaceholder': string;
  'timeline.form.typeLabel': string;
  'timeline.form.descriptionLabel': string;
  'timeline.form.descriptionPlaceholder': string;
  'timeline.form.yearLabel': string;
  'timeline.form.monthLabel': string;
  'timeline.form.dayLabel': string;
  'timeline.form.timeOfDayLabel': string;
  'timeline.form.manualTimeOverride': string;
  'timeline.form.customTimePlaceholder': string;
  'timeline.form.timeTextLabel': string;
  'timeline.form.timeLabelPlaceholder': string;
  'timeline.form.sortKeyLabel': string;
  'timeline.form.visibilityLabel': string;
  'timeline.form.visibilityAll': string;
  'timeline.form.visibilityGmOnly': string;
  'timeline.form.visibilityPlayers': string;
  'timeline.form.tagsLabel': string;
  'timeline.form.tagsPlaceholder': string;
  'timeline.form.linksLabel': string;
  'timeline.form.sessionLabel': string;
  'timeline.form.noSession': string;
  'timeline.form.cancelButton': string;
  'timeline.form.saveButton': string;
  'timeline.form.createButton': string;
  'timeline.form.suggestButton': string;
  'timeline.filters.title': string;
  'timeline.filters.resetAriaLabel': string;
  'timeline.filters.reset': string;
  'timeline.filters.searchPlaceholder': string;
  'timeline.filters.searchAriaLabel': string;
  'timeline.filters.typeGroupLabel': string;
  'timeline.filters.tagsGroupLabel': string;
  'timeline.filters.allSessions': string;
  'timeline.filters.myEventsOnly': string;
  'timeline.cursor.dayFormat': string;
  'timeline.cursor.editDateTooltip': string;
  'timeline.cursor.addDate': string;
  'timeline.cursor.nextDayTooltip': string;
  'timeline.cursor.nextDayButton': string;
  'timeline.cursor.markerTooltip': string;
  'timeline.cursor.monthShort': string;
  'timeline.cursor.saveTooltip': string;
  'timeline.cursor.cancelTooltip': string;
  'timeline.list.noDate': string;
  'timeline.list.addEventToDay': string;
  'timeline.list.draftBadge': string;
  'timeline.list.editTooltip': string;
  'timeline.list.deleteTooltip': string;
  'timeline.drafts.regionLabel': string;
  'timeline.drafts.title': string;
  'timeline.drafts.approveAriaLabel': string;
  'timeline.drafts.rejectAriaLabel': string;
  'timeline.panel.emptyTitle': string;
  'timeline.panel.emptySubtext': string;
  'timeline.panel.regionLabel': string;
  'timeline.panel.noTime': string;
  'timeline.panel.createEventAriaLabel': string;
  'timeline.panel.noFilterResults': string;
  'timeline.panel.emptySubtextGM': string;
  'timeline.panel.createEventButton': string;

  // === Campaign Sessions ===
  'campaignSessions.ariaLabel': string;
  'campaignSessions.title': string;
  'campaignSessions.newSession': string;
  'campaignSessions.titleLabel': string;
  'campaignSessions.namePlaceholder': string;
  'campaignSessions.dateLabel': string;
  'campaignSessions.active': string;
  'campaignSessions.complete': string;
  'campaignSessions.planned': string;
  'campaignSessions.start': string;
  'campaignSessions.completed': string;
  'campaignSessions.noSessions': string;

  // === Import Wizard ===
  'importWizard.title': string;
  'importWizard.sourceText': string;
  'importWizard.selectSource': string;
  'importWizard.sourceDndBeyondSoon': string;
  'importWizard.sourcePdfSoon': string;
  'importWizard.sourceImageSoon': string;
  'importWizard.extractingRelations': string;
  'importWizard.analyzingRelations': string;
  'importWizard.wave1': string;
  'importWizard.wave2': string;
  'importWizard.wave3': string;
  'importWizard.importError': string;
  'importWizard.source': string;
  'importWizard.dragDropHint': string;
  'importWizard.analyzing': string;
  'importWizard.analyze': string;
  'importWizard.conflictsFound': string;
  'importWizard.tableType': string;
  'importWizard.tableName': string;
  'importWizard.tableAction': string;
  'importWizard.tableNewName': string;
  'importWizard.strategySkip': string;
  'importWizard.strategyMerge': string;
  'importWizard.strategyRename': string;
  'importWizard.newNamePlaceholder': string;
  'importWizard.applying': string;
  'importWizard.apply': string;
  'importWizard.importPreview': string;
  'importWizard.importComplete': string;
  'importWizard.stepSource': string;
  'importWizard.stepUpload': string;
  'importWizard.stepConflicts': string;
  'importWizard.stepResult': string;
  'importWizard.strategyOverwrite': string;
  'importWizard.previewSheets': string;
  'importWizard.previewTokens': string;
  'importWizard.previewPages': string;
  'importWizard.previewNotes': string;
  'importWizard.resultAdded': string;
  'importWizard.resultOverwritten': string;
  'importWizard.resultSkipped': string;

  // === Bug Report additions ===
  'bugReport.sectionDescription': string;
  'bugReport.sectionElement': string;
  'bugReport.sectionContext': string;
  'bugReport.noDescription': string;
  'bugReport.roleGM': string;
  'bugReport.rolePlayer': string;

  // === SRD labels ===
  'srd.traits': string;
  'srd.actions': string;
  'srd.reactions': string;
  'srd.legendaryActions': string;
  'srd.castingTime': string;
  'srd.range': string;
  'srd.components': string;
  'srd.duration': string;
  'srd.resistances': string;
  'srd.immunities': string;
  'srd.senses': string;
  'srd.languages': string;
  'srd.cr': string;

  // === Spellbook ===
  'spellbook.title': string;
  'spellbook.empty': string;
  'spellbook.cantrips': string;
  'spellbook.concentration': string;

  // === Compendium Browser ===
  'compendium.equipment': string;
  'compendium.spells': string;
  'compendium.searchEquipment': string;
  'compendium.searchSpells': string;
  'compendium.noEquipment': string;
  'compendium.noSpells': string;
  'compendium.addToInventory': string;
  'compendium.addToSpellbook': string;
  'compendium.cost': string;
  'compendium.weight': string;
  'compendium.ac': string;
  'compendium.homebrew': string;
  'compendium.concentration': string;
  'compendium.properties': string;

  // === Chat additions ===
  'chat.search': string;
  'chat.searchPlaceholder': string;
  'chat.unpinMessage': string;
  'chat.pinMessage': string;
  'chat.gmPrefix': string;
  'chat.exportTitle': string;

  // === Character Wizard additions ===
  'wizard.characterNameLabel': string;
  'wizard.characterNamePlaceholder': string;
  'wizard.backgroundLabel': string;
  'wizard.backgroundPlaceholder': string;
  'wizard.raceLabel': string;
  'wizard.speed': string;
  'wizard.classLabel': string;
  'wizard.hitDie': string;
  'wizard.selectClassFirst': string;
  'wizard.noEquipmentPack': string;
  'wizard.equipped': string;

  // === Campaign Import ===
  'campaignImport.textInputHint': string;
  'campaignImport.placeholder': string;
  'campaignImport.uploadFile': string;
  'campaignImport.limitExceeded': string;
  'campaignImport.analyzing': string;
  'campaignImport.analyze': string;

  // === Homebrew Import ===
  'homebrewImport.title': string;
  'homebrewImport.inputLabel': string;
  'homebrewImport.placeholder': string;
  'homebrewImport.or': string;
  'homebrewImport.uploadPdf': string;
  'homebrewImport.extract': string;
  'homebrewImport.uploading': string;
  'homebrewImport.backgroundNote': string;
  'homebrewImport.extracting': string;
  'homebrewImport.preparingChunks': string;
  'homebrewImport.packageNameLabel': string;
  'homebrewImport.save': string;
  'homebrewImport.saving': string;
  'homebrewImport.complete': string;
  'homebrewImport.back': string;
  'homebrewImport.clear': string;
  'homebrewImport.chunk': string;
  'homebrewImport.entities': string;

  // === Entity Review ===
  'entityReview.selectAll': string;
  'entityReview.deselectAll': string;
  'entityReview.addManual': string;
  'entityReview.selectedCount': string;
  'entityReview.retryBelow': string;
  'entityReview.retry': string;

  // === Character Portraits ===
  'characterPortraits.changeColor': string;
  'characterPortraits.online': string;
  'characterPortraits.offline': string;
  // Visibility menu items
  'characterPortraits.visibility.always': string;
  'characterPortraits.visibility.gmOnly': string;
  'characterPortraits.visibility.hidden': string;
  'characterPortraits.visibilityLabel': string;
  'characterPortraits.sheetLabel': string;
  'characterPortraits.sheetVisibleToPlayers': string;
  'characterPortraits.sheetHiddenBadge': string;
  'characterPortraits.sizeLabel': string;
  'characterPortraits.size.small': string;
  'characterPortraits.size.medium': string;
  'characterPortraits.size.large': string;
  'characterPortraits.actingAs': string;
  'characterPortraits.hiddenFromPlayers': string;
  // Tooltips
  'characterPortraits.tooltip.actorFrame': string;
  'characterPortraits.tooltip.translucency': string;

  // === Text Input Overlay ===
  'textInput.ariaLabel': string;
  'textInput.placeholder': string;

  // === Dice Panel ===
  'dice.formulaPlaceholder': string;

  // === Lobby ===
  'lobby.sessionCodePlaceholder': string;

  // === Hook Errors ===
  'hookError.catalogFailed': string;
  'hookError.noEntitiesFound': string;
  'hookError.detailFailed': string;
  'hookError.noResponseBody': string;
  'hookError.relationFailed': string;
  'hookError.applyFailed': string;
  'hookError.fileTooLarge': string;
  'hookError.serverError': string;
  'hookError.networkError': string;
  'hookError.extractionFailed': string;
  'hookError.streamEnded': string;
  'hookError.retryFailed': string;
  'hookError.saveFailed': string;
  'hookError.loadFailed': string;
  'hookError.createFailed': string;
  'hookError.uploadFailed': string;
  'hookError.generationFailed': string;
  'hookError.canvasContext': string;
  'hookError.mapLoadFailed': string;
  'hookError.pageCreateTimeout': string;
  'hookError.lassoMinPoints': string;
  'hookError.exportFailed': string;
  'homebrew.defaultPackageName': string;

  // === Lazy chunk loading ===
  'error.chunkLoadFailed': string;

  // === Common additions ===
  'common.retry': string;

  // === Character HUD (066) ===
  'characterHud.pillHp': string;
  'characterHud.expand': string;
  'characterHud.collapse': string;
  'characterHud.openFullSheet': string;
  'characterHud.startCombat': string;
  'characterHud.expandDocked': string;
  'characterHud.showCharacter': string;
  'characterHud.combatBanner': string;
  'characterHud.combatBannerIdle': string;
  'characterHud.round': string;
  'characterHud.tileHp': string;
  'characterHud.tileAc': string;
  'characterHud.tileInit': string;
  'characterHud.tileConc': string;
  'characterHud.tileSpeed': string;
  'characterHud.actionAction': string;
  'characterHud.actionBonus': string;
  'characterHud.actionReaction': string;
  'characterHud.available': string;
  'characterHud.spent': string;
  'characterHud.attacks': string;
  'characterHud.spellSlots': string;
  'characterHud.effects': string;
  'characterHud.noConcentration': string;
  'characterHud.mockRoll': string;
  'characterHud.mockRollDesc': string;
  'characterHud.tabCombat': string;
  'characterHud.tabCharacter': string;
  'characterHud.tabCharShort': string;
  'characterHud.tabRoleplay': string;
  'characterHud.tabBuilder': string;
  'characterHud.backToHud': string;
  'characterHud.inDevelopment': string;
  'characterHud.stats': string;
  'characterHud.topSkills': string;
  'characterHud.saves': string;
  'characterHud.skills': string;
  'characterHud.searchSkills': string;
  'characterHud.pinSkill': string;
  'characterHud.unpinSkill': string;
  'characterHud.spells': string;
  'characterHud.detailCantrip': string;
  'characterHud.detailCircle': string;
  'characterHud.detailConcentration': string;
  'characterHud.detailRitual': string;
  'characterHud.detailCastingTime': string;
  'characterHud.detailRange': string;
  'characterHud.detailDuration': string;
  'characterHud.detailComponents': string;
  'characterHud.detailUses': string;
  'characterHud.detailHigherLevels': string;
  'characterHud.conditionAdd': string;
  'characterHud.conditionRemove': string;
  'characterHud.conditionRounds': string;
  'characterHud.conditionApply': string;
  'characterHud.collapseList': string;
  'characterHud.movement': string;
  'characterHud.movementHint': string;
  'characterHud.resetTurn': string;
  'characterHud.features': string;
  'characterHud.moreFeatures': string;
  'characterHud.spellsKnown': string;
  'characterHud.demoTitle': string;
  'characterHud.demoPill': string;
  'characterHud.demoMini': string;
  'characterHud.demoCombat': string;
  'characterHud.demoDocked': string;
  'characterHud.demoToggle': string;
  'characterHud.demoFullSheet': string;
  'characterHud.demoNpc': string;
  'characterHud.demoReset': string;
  'characterHud.npcAlly': string;
  'characterHud.npcWarm': string;
  'characterHud.npcNeutral': string;
  'characterHud.npcFoe': string;
  'characterHud.npcEnemy': string;
  'characterHud.close': string;
  'characterHud.hpTemp': string;
  'characterHud.level': string;
  'characterHud.storeCorrupted': string;

  // === Builder Mode (066 phase-4) ===
  'characterHud.builder.stepIdentity': string;
  'characterHud.builder.stepRace': string;
  'characterHud.builder.stepClass': string;
  'characterHud.builder.stepStats': string;
  'characterHud.builder.stepBackground': string;
  'characterHud.builder.stepSkills': string;
  'characterHud.builder.stepReview': string;
  'characterHud.builder.alignLG': string;
  'characterHud.builder.alignNG': string;
  'characterHud.builder.alignCG': string;
  'characterHud.builder.alignLN': string;
  'characterHud.builder.alignN': string;
  'characterHud.builder.alignCN': string;
  'characterHud.builder.alignLE': string;
  'characterHud.builder.alignNE': string;
  'characterHud.builder.alignCE': string;
  'characterHud.builder.methodPointBuy': string;
  'characterHud.builder.methodStandardArray': string;
  'characterHud.builder.methodManual': string;
  'characterHud.builder.identityTitle': string;
  'characterHud.builder.identitySubtitle': string;
  'characterHud.builder.labelName': string;
  'characterHud.builder.labelGender': string;
  'characterHud.builder.labelAlignment': string;
  'characterHud.builder.placeholderName': string;
  'characterHud.builder.raceTitle': string;
  'characterHud.builder.raceSubtitle': string;
  'characterHud.builder.subraceLabel': string;
  'characterHud.builder.raceSpeed': string;
  'characterHud.builder.raceSize': string;
  'characterHud.builder.raceLanguages': string;
  'characterHud.builder.raceTraits': string;
  'characterHud.builder.classTitle': string;
  'characterHud.builder.classSubtitle': string;
  'characterHud.builder.classSaves': string;
  'characterHud.builder.classProficiencies': string;
  'characterHud.builder.classSkillChoices': string;
  'characterHud.builder.classHitDie': string;
  'characterHud.builder.statsTitle': string;
  'characterHud.builder.statsSubtitle': string;
  'characterHud.builder.budgetRemaining': string;
  'characterHud.builder.budgetDone': string;
  'characterHud.builder.bgTitle': string;
  'characterHud.builder.bgSubtitle': string;
  'characterHud.builder.bgSkills': string;
  'characterHud.builder.bgFeature': string;
  'characterHud.builder.skillsTitle': string;
  'characterHud.builder.skillsSubtitle': string;
  'characterHud.builder.skillsCounter': string;
  'characterHud.builder.skillFromBg': string;
  'characterHud.builder.skillFromRace': string;
  'characterHud.builder.reviewTitle': string;
  'characterHud.builder.reviewSubtitle': string;
  'characterHud.builder.reviewIdentity': string;
  'characterHud.builder.reviewStats': string;
  'characterHud.builder.reviewCombat': string;
  'characterHud.builder.reviewSkills': string;
  'characterHud.builder.reviewProficiencies': string;
  'characterHud.builder.reviewSaves': string;
  'characterHud.builder.reviewErrors': string;
  'characterHud.builder.next': string;
  'characterHud.builder.prev': string;
  'characterHud.builder.save': string;
  'characterHud.builder.saved': string;
  'characterHud.builder.previewPlaceholder': string;
  'characterHud.builder.previewSkills': string;
  'characterHud.builder.previewProficiencies': string;
  'characterHud.builder.previewHp': string;
  'characterHud.builder.previewAc': string;
  'characterHud.builder.previewSpeed': string;
  'characterHud.builder.errEmptyName': string;
  'characterHud.builder.errNoRace': string;
  'characterHud.builder.errNoClass': string;
  'characterHud.builder.errPointBuy': string;
  'characterHud.builder.errStandardArray': string;
  'characterHud.builder.errManualRange': string;
  'characterHud.builder.errNoBackground': string;
  'characterHud.builder.errSkillsUnder': string;
  'characterHud.builder.errSkillsOver': string;
  'characterHud.builder.confirmOverwrite': string;
  'characterHud.builder.confirmDiscard': string;
  'characterHud.builder.skillOverlap': string;

  // === Write Infrastructure (073) ===
  'characterHud.writeTimeout': string;
  'characterHud.sheetDeleted': string;
  'characterHud.writeRejected': string;
  'characterHud.rollTimeout': string;
  'characterHud.restShort': string;
  'characterHud.restLong': string;
  'characterHud.deathSave': string;
  'characterHud.deathDying': string;
  'characterHud.deathStable': string;
  'characterHud.deathDead': string;
  'characterHud.deathSavesTally': string;
  'characterHud.hitDiceSpend': string;
  'characterHud.inspirationOn': string;
  'characterHud.inspirationOff': string;

  // === Character Mode (066 phase-2) ===
  'characterMode.navIdentity': string;
  'characterMode.navStats': string;
  'characterMode.navSkills': string;
  'characterMode.navDefenses': string;
  'characterMode.navAbilities': string;
  'characterMode.navSpells': string;
  'characterMode.navInventory': string;
  'characterMode.navProficiencies': string;
  'characterMode.navProgression': string;
  'characterMode.levelUp': string;
  'characterMode.passivePerception': string;
  'characterMode.passiveInvestigation': string;
  'characterMode.passiveInsight': string;
  'characterMode.filterAll': string;
  'characterMode.filterProficient': string;
  'characterMode.filterByAbility': string;
  'characterMode.breakdownTotal': string;
  'characterMode.breakdownStatMod': string;
  'characterMode.breakdownProficiency': string;
  'characterMode.breakdownExpertise': string;
  'characterMode.breakdownEffects': string;
  'characterMode.breakdownBase': string;
  'characterMode.spellDC': string;
  'characterMode.spellAttack': string;
  'characterMode.concentration': string;
  'characterMode.ritual': string;
  'characterMode.prepared': string;
  'characterMode.known': string;
  'characterMode.cantrips': string;
  'characterMode.spellLevel': string;
  'characterMode.noSpells': string;
  'characterMode.noFeatures': string;
  'characterMode.noItems': string;
  'characterMode.weight': string;
  'characterMode.currency': string;
  'characterMode.equipped': string;
  'characterMode.attunement': string;
  'characterMode.armor': string;
  'characterMode.weapons': string;
  'characterMode.tools': string;
  'characterMode.languages': string;
  'characterMode.currentLevel': string;
  'characterMode.levelManualHint': string;
  'characterMode.nextLevel': string;
  'characterMode.xpProgress': string;
  'characterMode.resistances': string;
  'characterMode.immunities': string;
  'characterMode.vulnerabilities': string;
  'characterMode.conditions': string;
  'characterMode.speeds': string;
  'characterMode.senses': string;
  'characterMode.classFeatures': string;
  'characterMode.raceFeatures': string;
  'characterMode.feats': string;
  'characterMode.allFeatures': string;
  'characterMode.inspiration': string;
  'characterMode.hitDice': string;
  'characterMode.mockLevelUp': string;
  'characterMode.search': string;
  'characterMode.proficiencyBonus': string;
  'characterMode.walkSpeed': string;
  'characterMode.unitFt': string;
  'characterMode.unitLbs': string;
  'characterMode.unitGp': string;
  'characterMode.armorClass': string;
  'characterMode.castingTime': string;
  'characterMode.range': string;
  'characterMode.castSpell': string;
  'characterMode.slotAriaLabel': string;
  'characterMode.useAriaLabel': string;
  'characterMode.available': string;
  'characterMode.spent': string;
  'characterMode.shortRest': string;
  'characterMode.longRest': string;
  'characterMode.asiOrFeat': string;
  'characterMode.attunementSlot': string;
  'characterMode.attunementFilled': string;
  'characterMode.attunementEmpty': string;
  'characterMode.bdModifier': string;
  'characterMode.bdValue': string;
  'characterMode.bdSave': string;
  'characterMode.bdMod': string;
  'characterMode.bdProficiency': string;
  'characterMode.bdExpertise': string;
  'characterMode.bdPassive': string;
  'characterMode.bdSkillMod': string;
  'characterMode.bdBaseAC': string;
  'characterMode.bdShield': string;
  'characterMode.bdItems': string;
  'characterMode.bdUnarmored': string;
  'characterMode.bdArmor': string;
  'characterMode.bdMiscAc': string;
  'characterMode.bdEffects': string;
  'characterMode.bdAttack': string;
  'characterMode.bdStatMod': string;
  'characterMode.bdSpellAttack': string;

  // === Roleplay Mode (066 phase-3) ===
  'roleplay.boardTitle': string;
  'roleplay.boardSubtitle': string;
  'roleplay.cardHero': string;
  'roleplay.cardTriada': string;
  'roleplay.cardManners': string;
  'roleplay.cardBackstory': string;
  'roleplay.cardGoals': string;
  'roleplay.cardNpc': string;
  'roleplay.cardFactions': string;
  'roleplay.cardSessionLog': string;
  'roleplay.cardMoments': string;
  'roleplay.catAll': string;
  'roleplay.catPersonality': string;
  'roleplay.catConnections': string;
  'roleplay.catPast': string;
  'roleplay.catPresent': string;
  'roleplay.catFuture': string;
  'roleplay.tagPersonality': string;
  'roleplay.tagConnections': string;
  'roleplay.tagPast': string;
  'roleplay.tagPresent': string;
  'roleplay.tagFuture': string;
  'roleplay.done': string;
  'roleplay.doneEsc': string;
  'roleplay.spanQuarter': string;
  'roleplay.spanThird': string;
  'roleplay.spanHalf': string;
  'roleplay.spanTwoThirds': string;
  'roleplay.spanFull': string;
  'roleplay.stylePlain': string;
  'roleplay.styleParchment': string;
  'roleplay.styleDark': string;
  'roleplay.addRow': string;
  'roleplay.addGoal': string;
  'roleplay.addNpc': string;
  'roleplay.addFaction': string;
  'roleplay.addMoment': string;
  'roleplay.addSession': string;
  'roleplay.duplicate': string;
  'roleplay.pin': string;
  'roleplay.unpin': string;
  'roleplay.changeType': string;
  'roleplay.deleteCard': string;
  'roleplay.deleteConfirmTitle': string;
  'roleplay.deleteConfirmDesc': string;
  'roleplay.changeTypeConfirm': string;
  'roleplay.momentPrologue': string;
  'roleplay.momentMinor': string;
  'roleplay.momentEncounter': string;
  'roleplay.momentDilemma': string;
  'roleplay.momentRevelation': string;
  'roleplay.momentTurning': string;
  'roleplay.momentFinale': string;
  'roleplay.relAlly': string;
  'roleplay.relWarm': string;
  'roleplay.relNeutral': string;
  'roleplay.relEnemy': string;
  'roleplay.searchPlaceholder': string;
  'roleplay.autoLayout': string;
  'roleplay.addCard': string;
  'roleplay.addCardTitle': string;
  'roleplay.addCardType': string;
  'roleplay.addCardContent': string;
  'roleplay.addCardSize': string;
  'roleplay.addCardStyle': string;
  'roleplay.addCardSubmit': string;
  'roleplay.addCardCancel': string;
  'roleplay.addCardTitlePlaceholder': string;
  'roleplay.addCardContentPlaceholder': string;
  'roleplay.editPlaceholder': string;
  'roleplay.noResults': string;
  'roleplay.labelIdeal': string;
  'roleplay.labelBond': string;
  'roleplay.labelFlaw': string;
  'roleplay.priorityArc': string;
  'roleplay.prioritySession': string;
  'roleplay.sessionNumber': string;
  'roleplay.sessionLabel': string;
  'roleplay.placeholderTitle': string;
  'roleplay.placeholderSummary': string;
  'roleplay.placeholderDescription': string;
  'roleplay.placeholderName': string;
  'roleplay.placeholderNotes': string;
  'roleplay.placeholderFaction': string;
  'roleplay.placeholderGoal': string;
  'roleplay.dragToReorder': string;
  'roleplay.editOptions': string;
  'roleplay.removeGoal': string;
  'roleplay.loyaltyLevel': string;
  'roleplay.footerCount': string;
  // T101 i18n (SUPER MEGA 2026-05-31) — stat + skill labels
  'characterMode.stat.str': string;
  'characterMode.stat.dex': string;
  'characterMode.stat.con': string;
  'characterMode.stat.int': string;
  'characterMode.stat.wis': string;
  'characterMode.stat.cha': string;
  'characterMode.saveAbbr': string;
  'characterMode.saveProficiencyHint': string;
  'characterMode.skill.acrobatics': string;
  'characterMode.skill.animalHandling': string;
  'characterMode.skill.arcana': string;
  'characterMode.skill.athletics': string;
  'characterMode.skill.deception': string;
  'characterMode.skill.history': string;
  'characterMode.skill.insight': string;
  'characterMode.skill.intimidation': string;
  'characterMode.skill.investigation': string;
  'characterMode.skill.medicine': string;
  'characterMode.skill.nature': string;
  'characterMode.skill.perception': string;
  'characterMode.skill.performance': string;
  'characterMode.skill.persuasion': string;
  'characterMode.skill.religion': string;
  'characterMode.skill.sleightOfHand': string;
  'characterMode.skill.stealth': string;
  'characterMode.skill.survival': string;
}

/** I18n context value provided by the Provider */
export interface I18nContextValue {
  /** Current locale */
  locale: Locale;
  /** Translation function */
  t: (key: keyof TranslationKeys) => string;
  /** Change the active locale (persists to localStorage) */
  setLocale: (locale: Locale) => void;
}
